/**
 * Extract an ISO 3166-1 alpha-2 country code and a human-readable region
 * (state / province) from a geocoder result.
 *
 * The shipping zone rule compares country codes, never display names, so this
 * has to return a code and never a translated label.
 */

const upper = value => (typeof value === 'string' && value.length > 0 ? value.toUpperCase() : null);

const componentTypes = component => (Array.isArray(component?.types) ? component.types : []);

// Google's new Places API returns { types, longText, shortText }.
// The older library returned { types, long_name, short_name }. Support both.
const longName = component => component?.longText || component?.long_name || null;
const shortName = component => component?.shortText || component?.short_name || null;

// One component resolver per provider, defined once. Each provider has two readers
// below — the country-and-region pair and the wider address-parts pair — and each was
// building its own copy of the same lookup. Two copies of a lookup is how the two
// quietly stop agreeing about where a country code comes from.
const googleByType = components => type => components.find(c => componentTypes(c).includes(type));

// Mapbox puts the parts of an address in `context`, EXCEPT when the searched-for
// feature is itself that part — a country search returns the country as the feature,
// with nothing above it in context. So both places have to be checked.
const mapboxByType = feature => {
  const context = Array.isArray(feature?.context) ? feature.context : [];
  const placeTypes = Array.isArray(feature?.place_type) ? feature.place_type : [];
  const fromContext = type =>
    context.find(c => typeof c?.id === 'string' && c.id.startsWith(`${type}.`));
  const fromFeature = type => (placeTypes.includes(type) ? feature : null);
  return type => fromContext(type) || fromFeature(type);
};

/**
 * @param {Array} addressComponents - place.addressComponents from the Google Places API
 * @returns {{countryCode: string|null, region: string|null}}
 */
export const countryAndRegionFromGoogle = addressComponents => {
  const components = Array.isArray(addressComponents) ? addressComponents : [];
  const byType = googleByType(components);

  const country = byType('country');
  const region = byType('administrative_area_level_1');

  return {
    countryCode: upper(shortName(country)),
    region: longName(region),
  };
};

/**
 * @param {Object} feature - a Mapbox geocoding feature (prediction)
 * @returns {{countryCode: string|null, region: string|null}}
 */
export const countryAndRegionFromMapbox = feature => {
  const byType = mapboxByType(feature);

  const country = byType('country');
  const region = byType('region');

  // A country's short_code is like 'us'. A region's is like 'US-CA', so never read it.
  const countryCode = country?.short_code || country?.properties?.short_code || null;

  return {
    countryCode: upper(countryCode),
    region: region?.text || null,
  };
};

export const PRECISION_STREET = 'street';
export const PRECISION_APPROXIMATE = 'approximate';

/**
 * A postcode is what a carrier rate needs. Having one is what makes an address
 * "street" precision; without one the address is fine to display but cannot be
 * quoted. See OPEN-4 in the CL-STOCK-01 brief.
 *
 * Exported because `buildLocationRecord` in stockLocations.js applies the same rule to
 * a saved record. It had its own copy of both the constants and this expression, so the
 * geocoder and the address book could have disagreed about what "street" means.
 */
export const precisionFor = postalCode => (postalCode ? PRECISION_STREET : PRECISION_APPROXIMATE);

/**
 * @param {Array} addressComponents - place.addressComponents from the Google Places API
 * @returns {{countryCode: string|null, region: string|null, city: string|null,
 *            postalCode: string|null, precision: string}}
 */
export const addressPartsFromGoogle = addressComponents => {
  const components = Array.isArray(addressComponents) ? addressComponents : [];
  const byType = googleByType(components);

  // 'locality' is the usual city. UK addresses often carry 'postal_town' instead.
  const city = longName(byType('locality')) || longName(byType('postal_town'));
  const postalCode = longName(byType('postal_code'));

  return {
    ...countryAndRegionFromGoogle(components),
    city: city || null,
    postalCode: postalCode || null,
    precision: precisionFor(postalCode),
  };
};

/**
 * @param {Object} feature - a Mapbox geocoding feature (prediction)
 * @returns {{countryCode: string|null, region: string|null, city: string|null,
 *            postalCode: string|null, precision: string}}
 */
export const addressPartsFromMapbox = feature => {
  const byType = mapboxByType(feature);
  const postalCode = byType('postcode')?.text || null;

  return {
    ...countryAndRegionFromMapbox(feature),
    city: byType('place')?.text || null,
    postalCode,
    precision: precisionFor(postalCode),
  };
};
